// components/workspace-timeline/WorkspaceTimelineRuler.tsx
import React, { useMemo } from 'react';
import { clamp } from './presentation';

interface WorkspaceTimelineRulerProps {
  rulerRef: React.RefObject<HTMLDivElement | null>;
  rulerTickSeconds: number[];
  rulerStepSeconds: number;
  timelineRenderRangeStartMs: number;
  timelineRenderRangeEndMs: number;
  timelineRenderRangeMs: number;
  clampedCursorMs: number;
  cursorPercent: number;
  dragSnapGuidePercent: number | null;
  onMouseDown: React.MouseEventHandler<HTMLDivElement>;
}

interface WorkspaceTimelineRulerTickItem {
  key: string;
  label: string;
  containerStyle: React.CSSProperties;
  markStyle: React.CSSProperties;
  labelStyle: React.CSSProperties;
}

const TIMELINE_RULER_ROOT_STYLE: React.CSSProperties = { userSelect: 'none' };
const TIMELINE_RULER_CENTER_LINE_STYLE: React.CSSProperties = {
  position: 'absolute',
  left: 0,
  right: 0,
  top: '10px',
  height: '1px',
  backgroundColor: '#262626',
};
const TIMELINE_RULER_TICK_CONTAINER_BASE_STYLE: React.CSSProperties = {
  position: 'absolute',
  top: '4px',
  transform: 'translateX(-0.5px)',
  pointerEvents: 'none',
};
const TIMELINE_RULER_TICK_MARK_BASE_STYLE: React.CSSProperties = {
  width: '1px',
  backgroundColor: '#5A5A5A',
};
const TIMELINE_RULER_TICK_LABEL_BASE_STYLE: React.CSSProperties = {
  marginTop: '1px',
  transform: 'translateX(-50%)',
  fontSize: 11,
  lineHeight: '12px',
  color: '#8E8E8E',
  whiteSpace: 'nowrap',
};
const TIMELINE_RULER_SNAP_GUIDE_BASE_STYLE: React.CSSProperties = {
  position: 'absolute',
  top: '1px',
  bottom: '0px',
  width: '2px',
  transform: 'translateX(-1px)',
  borderRadius: '1px',
  backgroundColor: 'rgba(236,236,236,0.5)',
  pointerEvents: 'none',
};
const TIMELINE_RULER_CURSOR_CONTAINER_BASE_STYLE: React.CSSProperties = {
  position: 'absolute',
  top: '-3px',
  width: '0px',
  transform: 'translateX(-1px)',
  pointerEvents: 'none',
  zIndex: 5,
};
const TIMELINE_RULER_CURSOR_MARKER_STYLE: React.CSSProperties = {
  position: 'absolute',
  left: '-4px',
  top: 0,
  width: '10px',
  height: '10px',
  borderRadius: '9999px',
  backgroundColor: '#ECECEC',
  boxShadow: '0 0 0 2px #1B1B1B',
};

export const WorkspaceTimelineRuler = React.memo(({
  rulerRef,
  rulerTickSeconds,
  rulerStepSeconds,
  timelineRenderRangeStartMs,
  timelineRenderRangeEndMs,
  timelineRenderRangeMs,
  clampedCursorMs,
  cursorPercent,
  dragSnapGuidePercent,
  onMouseDown,
}: WorkspaceTimelineRulerProps) => {
  const ariaValueMin = useMemo(
    () => Math.round(timelineRenderRangeStartMs),
    [timelineRenderRangeStartMs]
  );
  const ariaValueMax = useMemo(
    () => Math.round(timelineRenderRangeEndMs),
    [timelineRenderRangeEndMs]
  );
  const ariaValueNow = useMemo(
    () => Math.round(clampedCursorMs),
    [clampedCursorMs]
  );
  const tickItems = useMemo<WorkspaceTimelineRulerTickItem[]>(() => {
    const majorStepSeconds = Math.max(1, rulerStepSeconds * 2);
    return rulerTickSeconds.map((second) => {
      const positionPercent = timelineRenderRangeMs > 0
        ? clamp((second * 1000 / timelineRenderRangeMs) * 100, 0, 100)
        : 0;
      const isMajorTick = second % majorStepSeconds === 0;
      return {
        key: `ruler-tick-${second}`,
        label: `${second}s`,
        containerStyle: {
          ...TIMELINE_RULER_TICK_CONTAINER_BASE_STYLE,
          left: `${positionPercent}%`,
        },
        markStyle: {
          ...TIMELINE_RULER_TICK_MARK_BASE_STYLE,
          height: isMajorTick ? '7px' : '5px',
          opacity: isMajorTick ? 0.9 : 0.55,
        },
        labelStyle: {
          ...TIMELINE_RULER_TICK_LABEL_BASE_STYLE,
          opacity: isMajorTick ? 0.95 : 0,
        },
      };
    });
  }, [rulerStepSeconds, rulerTickSeconds, timelineRenderRangeMs]);
  const snapGuideStyle = useMemo<React.CSSProperties | null>(() => {
    if (dragSnapGuidePercent === null) return null;
    return {
      ...TIMELINE_RULER_SNAP_GUIDE_BASE_STYLE,
      left: `${dragSnapGuidePercent}%`,
    };
  }, [dragSnapGuidePercent]);
  const cursorMarkerStyle = useMemo<React.CSSProperties>(() => ({
    ...TIMELINE_RULER_CURSOR_CONTAINER_BASE_STYLE,
    left: `${clamp(cursorPercent, 0, 100)}%`,
  }), [cursorPercent]);

  return (
    <div
      ref={rulerRef}
      role="slider"
      aria-label="Timeline ruler"
      aria-valuemin={ariaValueMin}
      aria-valuemax={ariaValueMax}
      aria-valuenow={ariaValueNow}
      onMouseDown={onMouseDown}
      className="relative h-7 min-w-0 cursor-ew-resize select-none"
      style={TIMELINE_RULER_ROOT_STYLE}
    >
      <div style={TIMELINE_RULER_CENTER_LINE_STYLE} />
      {tickItems.map((tick) => (
        <div key={tick.key} style={tick.containerStyle}>
          <div style={tick.markStyle} />
          <div style={tick.labelStyle}>{tick.label}</div>
        </div>
      ))}
      {snapGuideStyle !== null && <div style={snapGuideStyle} />}
      <div style={cursorMarkerStyle}>
        <div style={TIMELINE_RULER_CURSOR_MARKER_STYLE} />
      </div>
    </div>
  );
});

WorkspaceTimelineRuler.displayName = 'WorkspaceTimelineRuler';
